import React, { FC } from 'react';
import { useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { Observer } from 'mobx-react';
import { toast } from 'react-toastify';

import { ThemeProps } from '@styles/theme';
import { useUsersGridUIStore, useUserPermissionsUIStore } from '@core/useStores';
import { ToastMessage } from '@shared/components/Toast';
import { messages } from '@shared/shared.messages';
import classNames from 'classnames';
import ImportIcon from '@assets/import.svg';
import { useTheme } from '@mui/material/styles';
import { Button } from '@shared/components/Button';

const useStyles = createUseStyles((theme: ThemeProps) => ({

    buttonImport: {
        width: 'auto',
        height: theme.spacing(10),
        fontFamily: theme.font.secondary,
        fontSize: 14,
        lineHeight: 1.4,
        fontWeight: 'normal',
        padding: 8,
        borderRadius: theme.radius / 2,
        background: theme.colors.icon_background,
        // backgroundColor: 'transparent',
        color: theme.colors.violet_primary,
        '&:hover': {
            background: theme.colors.transp_violet,
        },
        '&.span':{
            marginRight: 0,
            marginLeft: 0,
        },
    },

    ImportIcon: {
        margin: '0 2px',
        height: 22,
        width: 22,
    },
}));

export const UsersImport: FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const usersUIStore = useUsersGridUIStore();
    const userPermissionsUIStore = useUserPermissionsUIStore();
    const canImport = userPermissionsUIStore.isSuperAdmin || userPermissionsUIStore.isMultiAdmin;

    const inputRef = useRef({} as HTMLInputElement);

    const onClick = () => {
        // open file input box on click of another element
        inputRef.current.click();
    }

    const getFileExtension = (fileName: string) => {
        return fileName.split('.').pop();
    };

    const importUsers = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileObj = event.target.files && event.target.files[0];
        if (!fileObj) {
            return;
        }

        if (getFileExtension(fileObj.name) != 'csv') {
            toast.error(
                <ToastMessage
                    message={messages['import.toast.unsupported']}
                    type='error'
                />
            );

            return;
        }

        toast.success(
            <ToastMessage
                message={messages['import.toast.inProgress']}
                type='success'
            />
        );
        // const companyId = userProfileUIStore.userInfo.companyId;
        const result = await usersUIStore.importUsers({ file: fileObj });
        if (result.success) {
            // Forces a refresh of the page to load the new imported data
            window.location.reload();
        } else {
            const error = result?.error?.body?.messages
                ? result.error.body.messages[0] : 'Unspecified error';
            toast.error(
                <ToastMessage
                    message={error}
                    type='error'
                />
            );
        }
    }

    return (
        <Observer>
            {() => {
                const { isImporting } = usersUIStore;

                return (
                    canImport && <>
                        <input
                            style={{ display: 'none' }}
                            ref={inputRef}
                            type="file"
                            accept=".csv"
                            onChange={importUsers}
                        />
                        <Button
                            onClick={onClick}
                            className={classNames(styles.buttonImport)}
                            disabled={!canImport}
                            loading={isImporting}
                        >
                            <img src={ImportIcon} />
                        </Button>
                    </>
                )
            }}
        </Observer>
    )
}
